import React, { useState, useEffect } from 'react';
import { Typography, Box, Grid, Card, CardContent, CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  ShoppingCart as OrderIcon,
  Inventory as ProductIcon,
  People as UserIcon,
  Store as BranchIcon,
  WifiTethering as ActiveUsersIcon,
} from '@mui/icons-material';

import { fetchOrders } from '../ordersApi';
import { fetchProducts } from '../productsApi';
import { fetchUsers } from '../usersApi';
import { fetchBranches } from '../branchesApi';
import { fetchActiveSessionsCount } from '../activeSessionsApi';

const StatCard = ({ title, value, icon, color }) => (
  <Card sx={{ height: '100%' }}>
    <CardContent>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography color="textSecondary" gutterBottom variant="overline">
            {title}
          </Typography>
          <Typography variant="h4">
            {value}
          </Typography>
        </Box>
        <Box sx={{ 
          bgcolor: `${color}.light`, 
          color: `${color}.main`, 
          p: 1, 
          borderRadius: '50%',
          display: 'flex'
        }}>
          {icon}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

export default function Dashboard() {
  const { t } = useTranslation();
  const [stats, setStats] = useState({
    orders: 0,
    products: 0,
    users: 0,
    branches: 0,
    activeSessions: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let interval;
    async function loadStats() {
      try {
        const [ordersData, productsData, usersData, branchesData, activeSessionsCount] = await Promise.all([
          fetchOrders().catch(() => []),
          fetchProducts().catch(() => []),
          fetchUsers().catch(() => []),
          fetchBranches().catch(() => []),
          fetchActiveSessionsCount(),
        ]);

        setStats({
          orders: ordersData.length,
          products: productsData.length,
          users: usersData.length,
          branches: branchesData.length,
          activeSessions: activeSessionsCount,
        });
      } catch (error) {
        console.error('Error loading dashboard stats:', error);
      } finally {
        setLoading(false);
      }
    }
    loadStats();

    // Poll active sessions count every 30 seconds
    interval = setInterval(async () => {
      try {
        const activeCount = await fetchActiveSessionsCount();
        setStats(prev => ({ ...prev, activeSessions: activeCount }));
      } catch (e) {
        // Optionally handle error
      }
    }, 60000);

    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 4 }}>
        {t('Dashboard')}
      </Typography>
      
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard 
            title={t('Total_Orders')} 
            value={stats.orders} 
            icon={<OrderIcon />} 
            color="primary" 
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard 
            title={t('Total_Products')} 
            value={stats.products} 
            icon={<ProductIcon />} 
            color="success" 
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard 
            title={t('Total_Users')} 
            value={stats.users} 
            icon={<UserIcon />} 
            color="warning" 
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard 
            title={t('Total_Branches')} 
            value={stats.branches} 
            icon={<BranchIcon />} 
            color="info" 
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard 
            title={t('Live_Active_Users')} 
            value={stats.activeSessions} 
            icon={<ActiveUsersIcon />} 
            color="secondary" 
          />
        </Grid>
      </Grid>

      <Box sx={{ mt: 4 }}>
        <Typography variant="h6" gutterBottom>
          {t('WelcomeMessage')}
        </Typography>
        <Typography color="textSecondary">
          {t('UseSidebarMessage')}
        </Typography>
      </Box>
    </Box>
  );
}